import React, { useCallback, useContext, useState } from 'react';
import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
} from 'antd';
import dayjs from 'dayjs';

import SecurityGroupSelectorDialog from '@totem/components/documents/dialogs/securityGroupSelectorDialog';
import { DocumentSummaryEntry } from '@totem/components/documents/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import { USER_ROLES } from '@totem/components/userContext/accountUtilities';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNull } from '@totem/utilities/common';
import { DOCUMENTS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
const styles = {
  form: {
    width: '100%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
  iconButton: {
    margin: '0 2px',
  },
  removeIcon: {
    color: '#1890ff',
  },
};
type Props = {
  visible: boolean;
  onClose: (refresh: boolean) => void;
  document: DocumentSummaryEntry;
};
const DocumentEditDialog = ({ visible, onClose, document }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [doc, setDoc] = useState<DocumentSummaryEntry>(document);
  const [showTagEntry, setShowTagEntry] = useState<boolean>(false);
  const [showSecurityGroupsDialog, setShowSecurityGroupsDialog] =
    useState<boolean>(false);
  const [tmpTag, setTmpTag] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const sendDocumentUpdate = useCallback(
    async (documentUpdate: DocumentSummaryEntry) => {
      if (!isSending) {
        fetch(`${DOCUMENTS_ENDPOINT}/${documentUpdate.document.id}`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(documentUpdate),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            onClose(true);
          }
        });
      }
    }, // End async
    [],
  );
  const handleSubmit = () => {
    sendDocumentUpdate(doc);
  };
  const roleOptions = USER_ROLES.filter(
    (role) => role.value <= userProfile.organization.role,
  ).map((role) => ({
    value: role.value,
    label: role.label,
  }));
  if (isNull(document)) {
    return <span />;
  }
  return (
    <Modal
      open={visible}
      onCancel={() => onClose(false)}
      title={<ModalTitle>Document Edit: {doc.document.displayName}</ModalTitle>}
      confirmLoading={false}
      width={'30%'}
      footer={
        <ModalFooter>
          <Button onClick={() => onClose(false)}>Close</Button>
          <Button type={'primary'} onClick={handleSubmit}>
            Submit
          </Button>
        </ModalFooter>
      }
    >
      <div>
        <Form layout="vertical" style={styles.form} onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="displayName" label="Name">
                <Input
                  value={doc.document.displayName}
                  onChange={(evt) => {
                    setDoc((prevState) => ({
                      ...prevState,
                      document: {
                        ...prevState.document,
                        displayName: evt.target.value,
                      },
                    }));
                  }}
                />
              </Form.Item>
              {/* Date Picker */}
              <Form.Item name="expirationDate" label="Expiration Date">
                <DatePicker
                  allowClear={false}
                  value={dayjs(doc.document.expirationDate)}
                  onChange={(time) => {
                    setDoc((prevState) => ({
                      ...prevState,
                      document: {
                        ...prevState.document,
                        expirationDate: time.toISOString(),
                      },
                    }));
                  }}
                  format={'L LT'}
                  style={{ width: '200px' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    Tags&nbsp;&nbsp;
                    <Button
                      icon={<PlusCircleOutlined />}
                      onClick={() => setShowTagEntry(true)}
                      disabled={showTagEntry}
                    />{' '}
                  </span>
                }
                style={styles.formItem}
              >
                {showTagEntry && (
                  <Flex justify="flex-start">
                    <Input
                      value={tmpTag}
                      onChange={(evt) => setTmpTag(evt.target.value)}
                      onBeforeInput={(evt) => {
                        const nativeEvent = evt.nativeEvent as InputEvent;
                        if (
                          nativeEvent.data &&
                          nativeEvent.data.includes(',')
                        ) {
                          evt.preventDefault(); // Prevent inserting the character
                        }
                      }}
                    />
                    <Button onClick={() => setShowTagEntry(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        setDoc((prevState) => ({
                          ...prevState,
                          document: {
                            ...prevState.document,
                            tags: [...prevState.document.tags, tmpTag],
                          },
                        }));

                        setTmpTag('');

                        setShowTagEntry(false);
                      }}
                    >
                      Add
                    </Button>
                  </Flex>
                )}
                {doc.document.tags.map((chk) => (
                  <div key={chk}>
                    <CloseCircleOutlined
                      onClick={() => {
                        setDoc((prevState) => ({
                          ...prevState,
                          document: {
                            ...prevState.document,
                            tags: prevState.document.tags.filter(
                              (tag) => tag !== chk,
                            ),
                          },
                        }));
                      }}
                    />
                    &nbsp;&nbsp;{chk}
                  </div>
                ))}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Minimum role required to view document"
                style={styles.formItem}
              >
                <Select
                  onChange={(role) => {
                    setDoc((prevState) => ({
                      ...prevState,
                      document: {
                        ...prevState.document,
                        minimumRole: role,
                      },
                    }));
                  }}
                  value={doc.document.minimumRole}
                  options={roleOptions}
                />
              </Form.Item>
              {permissions.internal.hasPermissions(userProfile) && (
                <Form.Item label="Internal Only" style={styles.formItem}>
                  <Switch
                    defaultChecked={doc.document.internalOnly}
                    value={doc.document.internalOnly}
                    onChange={(isChecked) => {
                      setDoc((prevState) => ({
                        ...prevState,
                        document: {
                          ...prevState.document,
                          internalOnly: isChecked,
                        },
                      }));
                    }}
                  />
                </Form.Item>
              )}
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    Additional Security&nbsp;&nbsp;
                    <Button
                      icon={<PlusCircleOutlined />}
                      onClick={() => setShowSecurityGroupsDialog(true)}
                    />{' '}
                  </span>
                }
                style={styles.formItem}
              >
                {doc.document.securityGroups.length > 0 && (
                  <div style={{ color: '#FF0000', fontSize: 'small' }}>
                    (user must be a member of one of the following groups to
                    groups to view):
                  </div>
                )}
                {doc.document.securityGroups.map((chk) => (
                  <div key={chk}>
                    <CloseCircleOutlined
                      onClick={() => {
                        setDoc((prevState) => ({
                          ...prevState,
                          document: {
                            ...prevState.document,
                            securityGroups:
                              prevState.document.securityGroups.filter(
                                (group) => group !== chk,
                              ),
                          },
                        }));
                      }}
                    />
                    &nbsp;&nbsp;
                    {chk}
                  </div>
                ))}
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {showSecurityGroupsDialog && (
          <SecurityGroupSelectorDialog
            onClose={() => setShowSecurityGroupsDialog(false)}
            onSubmit={(securityGroup) => {
              if (doc.document.securityGroups.includes(securityGroup.name)) {
                setDoc((prevState) => ({
                  ...prevState,
                  document: {
                    ...prevState.document,
                    securityGroups: prevState.document.securityGroups.filter(
                      (group) => group !== securityGroup.name,
                    ),
                  },
                }));
              } else {
                setDoc((prevState) => ({
                  ...prevState,
                  document: {
                    ...prevState.document,
                    securityGroups: [
                      ...prevState.document.securityGroups,
                      securityGroup.name,
                    ],
                  },
                }));
              }
              setShowSecurityGroupsDialog(false);
            }}
            visible={showSecurityGroupsDialog}
          />
        )}
      </div>
    </Modal>
  );
};
export default DocumentEditDialog;
